import { isEmpty } from 'ramda';
import React, { useCallback, useRef, useState } from 'react';
import JustificationContainer from '~/components/atom/JustificationContainer';

import { isValid } from '~/util/Validation/Tag';
import AnimatedCheckbox from '~/components/molecule/AnimatedCheckbox';
import DebouncedInput from '~/components/molecule/DebouncedInput';
import { RealworksRelatieSyncType } from '~/graphql/types';
import { Label } from '~/components/atom/Typography';

const text = {
  tagFieldPlaceholder: 'Alleen kleine letters, cijfers en (-) zijn toegestaan',
  errorLabel: 'Vul een geldige tag in',
};

type Attribute = {
  id: number;
  description: string | null;
  tag: string | null;
  checked: boolean;
};

export type Props = {
  row: Attribute;
  dataTestId?: string;
  className?: string;
  selectable: boolean;
  syncType: RealworksRelatieSyncType;
  onChange: (value: string | boolean | null, field: string) => void;
};

const KenmerkRow: React.FCC<Props> = ({
  dataTestId,
  row: { id, description, tag, checked },
  onChange,
  syncType,
  selectable,
}) => {
  const cachedValue = useRef(tag).current || '';
  const [hasError, setHasError] = useState(false);

  const onTagChange = value => {
    setHasError(!isEmpty(value) && !isValid(value));
    onChange(value, 'tag');
  };

  const onCheckboxChange = useCallback(
    () => onChange(!checked, 'checked'),
    [checked, onChange],
  );

  const externalErrors = hasError ? [text.errorLabel] : undefined;
  const hideInput =
    syncType === RealworksRelatieSyncType.FullExcludingKenmerken && checked;

  const appearance = (() => {
    switch (syncType) {
      case RealworksRelatieSyncType.FullExcludingKenmerken:
        return 'danger';
      case RealworksRelatieSyncType.LimitToKenmerken:
        return 'success';
      default:
        return undefined;
    }
  })();

  return (
    <React.Fragment data-testid={dataTestId} data-objectid={id}>
      <JustificationContainer
        direction="column"
        gap="xxxs"
        width="100%"
        justification="center"
      >
        <AnimatedCheckbox
          label={
            <Label inline fontWeight="semiBold" size="base">
              {description}
            </Label>
          }
          value={checked}
          selectable={selectable}
          onChange={onCheckboxChange}
          name="row-checkbox"
          icon={
            syncType === RealworksRelatieSyncType.FullExcludingKenmerken
              ? 'cross'
              : undefined
          }
          appearance={appearance}
        />
      </JustificationContainer>

      <JustificationContainer
        direction="column"
        gap="xxxs"
        width="100%"
        justification="center"
      >
        {!hideInput && (
          <DebouncedInput
            defaultValue={cachedValue}
            onDebouncedChange={onTagChange}
            placeholder={text.tagFieldPlaceholder}
            externalErrors={externalErrors}
            name="row-input-field"
            width="100%"
          />
        )}
      </JustificationContainer>
    </React.Fragment>
  );
};

export default KenmerkRow;
